// src/modules/feedbacks/components/FeedbackStatsCard.tsx
import React from 'react';
import { Card, Row, Col, Progress, Typography, Space } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { FeedbackStats } from '@/services/api/feedbacks';
import { getRatingColor, calculateRatingPercentages } from '@/utils/feedbackUtils';

const { Text, Title } = Typography;

interface FeedbackStatsCardProps {
  stats: FeedbackStats;
}

export const FeedbackStatsCard: React.FC<FeedbackStatsCardProps> = ({ stats }) => {
  const percentages = calculateRatingPercentages(stats.distribution);

  return (
    <Card title="Детальная статистика отзывов" className="mb-4">
      <Row gutter={[24, 24]}>
        {/* Средняя оценка */}
        <Col span={8}>
          <div className="text-center">
            <div className="text-6xl font-bold mb-2" style={{ color: getRatingColor(stats.average_rating) }}>
              {stats.average_rating.toFixed(1)}
            </div>
            <div className="mb-2">
              <StarFilled style={{ fontSize: 24, color: getRatingColor(stats.average_rating) }} />
            </div>
            <Text type="secondary">Средняя оценка</Text>
            <div className="mt-2">
              <Text type="secondary" className="text-sm">
                На основе {stats.total_reviews} отзывов
              </Text>
            </div>
          </div>
        </Col>

        {/* Распределение оценок */}
        <Col span={16}>
          <Title level={5} className="mb-4">Распределение оценок</Title>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {[3, 2, 1].map((rating) => {
              const count = stats.distribution[String(rating) as '1' | '2' | '3'];
              const percent = percentages[rating.toString()];
              
              const ratingLabels: { [key: number]: string } = {
                3: 'Отлично',
                2: 'Хорошо',
                1: 'Плохо'
              };
              
              return (
                <Row key={rating} align="middle" gutter={16}>
                  <Col span={3}>
                    <Text strong>{ratingLabels[rating]}</Text>
                  </Col>
                  <Col span={18}>
                    <Progress
                      percent={percent}
                      strokeColor={getRatingColor(rating)}
                      showInfo={false}
                      size="small"
                    />
                  </Col>
                  <Col span={3} className="text-right">
                    <Text type="secondary">{count}</Text>
                  </Col>
                </Row>
              );
            })}
          </Space>
        </Col>
      </Row>

      {/* Дополнительная информация */}
      <Row gutter={16} className="mt-6 pt-6 border-t">
        <Col span={8} className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats.approved}
          </div>
          <Text type="secondary">Одобрено</Text>
        </Col>
        <Col span={8} className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {stats.pending_moderation}
          </div>
          <Text type="secondary">На модерации</Text>
        </Col>
        <Col span={8} className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {((stats.approved / stats.total_reviews) * 100).toFixed(0)}%
          </div>
          <Text type="secondary">Процент одобрения</Text>
        </Col>
      </Row>
    </Card>
  );
};